"use client";

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";

import Button from "@/components/atoms/Button";
import Icon from "@/components/atoms/Icon";
import Sprite from "@/components/atoms/Sprite";
import { useTranslation } from "@/i18n/client";
import { WORK_ID_JOB_APPLICATION_COOKIE_NAME } from "@/lib/constants/workIdJobApplication";
import { getUtmSourceFromSessionStorage } from "@/lib/helpers/utmParams";
import { AllowedLanguages, WorkIdJobApplicationCookie } from "@/types/generic";

interface LoginSectionProps {
  jobId?: string;
  locale: AllowedLanguages;
  officeKey?: string;
  type?: string;
}

export const LoginSection = ({
  locale,
  jobId,
  officeKey,
  type,
}: LoginSectionProps) => {
  const { t } = useTranslation(locale);
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const handleApplyViaWorkID = async () => {
    try {
      setIsLoading(true);

      const workIdJobApplicationCookie: WorkIdJobApplicationCookie = {
        jobId,
        locale,
        officeKey: officeKey,
        source: getUtmSourceFromSessionStorage(),
        type,
      };

      document.cookie = `${WORK_ID_JOB_APPLICATION_COOKIE_NAME}=${encodeURIComponent(
        JSON.stringify(workIdJobApplicationCookie),
      )}; path=/; max-age=600; samesite=lax`;

      await signIn("workid");
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative overflow-hidden bg-yellow-600 p-6 lg:p-10">
      <div className="relative z-1 grid grid-cols-1 text-center">
        <div>
          <div className="mb-6 inline-flex aspect-square w-16 items-center justify-center rounded-full bg-white">
            <Icon name="profile" className="h-12 w-12 text-black-400" />
          </div>
          <h5 className="!mb-1">{t("pages.apply.login_section.title")}</h5>
          <div className="text-sm text-black-400">
            {t("pages.apply.login_section.description")}
          </div>
          <div className="flex justify-center gap-4">
            <Button
              as="link"
              href={`/${locale}/myvivaldis`}
              className="mt-8 min-w-[180px]"
              arrow={false}
            >
              {t("pages.apply.login_section.login_btn")}
            </Button>
            <Button
              variant="solid"
              color="white"
              icon="workid"
              isLoading={isLoading}
              disabled={isLoading}
              hasLoader
              onClick={handleApplyViaWorkID}
              className="mt-8 min-w-[180px] gap-2"
              arrow={false}
            >
              {t("pages.apply.login_section.apply_via_work_id_btn")}
            </Button>
          </div>
          {searchParams.get("error") && (
            <div className="mt-2 text-sm text-red">
              {searchParams.get("error")}
            </div>
          )}
          {searchParams.get("error_description") && (
            <div className="mt-1 text-sm text-red">
              {searchParams.get("error_description")}
            </div>
          )}
        </div>
      </div>
      <Sprite className="right-0 top-[-40px] w-[400px] translate-x-[40px]" />
    </div>
  );
};
